import React, { useState, useEffect } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { formatPhoneNumber } from '../../../../../utils/formatters';
import { validatePhone, validateRequired } from '../../../../../utils/validation';
import styles from '../../WizardSteps.module.css';

// Role options for the user
// Maps to reference_roles: Owner (dd5dce6f) has project.admin, Project Manager (54a786d3) has project.manage
const OWNER_ROLE_ID = 'dd5dce6f-da05-4c9c-9dcb-5c7e3e82e662';
const PM_ROLE_ID = '54a786d3-d50b-4309-9b97-2b65c06aa089';
const ROLE_OPTIONS = [
  { value: OWNER_ROLE_ID, label: 'בעל/ת הבית' },
  { value: PM_ROLE_ID, label: 'מנהל/ת פרויקט' },
  { value: PM_ROLE_ID + '_architect', label: 'אדריכל/ית', roleId: PM_ROLE_ID },
  { value: PM_ROLE_ID + '_contractor', label: 'קבלן/ית', roleId: PM_ROLE_ID },
  { value: PM_ROLE_ID + '_other', label: 'אחר', roleId: PM_ROLE_ID },
];

const UserInfoStep = ({ data, onChange }) => {
  const session = useSession();

  // Initialize with existing data or defaults
  const [formData, setFormData] = useState({
    fullName: data?.fullName || '',
    phone: data?.phone || '',
    email: session?.user?.email || '',
    role: data?.role || '',
  });

  const [errors, setErrors] = useState({});

  // Update parent when form data changes
  useEffect(() => {
    onChange(formData);
  }, [formData, onChange]);

  const handleChange = field => e => {
    let value = e.target.value;

    // Format phone number as user types
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate field on blur
  const handleBlur = field => () => {
    let error = null;

    switch (field) {
      case 'fullName':
        error = validateRequired(formData.fullName, 'שם מלא');
        break;
      case 'phone':
        error = validatePhone(formData.phone);
        break;
      case 'role':
        error = validateRequired(formData.role, 'תפקיד');
        break;
      default:
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  return (
    <div className={styles.stepContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>פרטים אישיים</h1>
        <p className={styles.subtitle}>ספר לנו קצת על עצמך</p>
      </div>

      {/* Form Fields */}
      <div className={styles.form}>
        <div className={styles.field}>
          <label className={styles.label}>שם מלא</label>
          <input
            type="text"
            className={`${styles.input} ${errors.fullName ? styles.inputError : ''}`}
            value={formData.fullName}
            onChange={handleChange('fullName')}
            onBlur={handleBlur('fullName')}
            placeholder="הכנס את שמך המלא"
          />
          {errors.fullName && <span className={styles.error}>{errors.fullName}</span>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>טלפון</label>
          <input
            type="tel"
            className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
            value={formData.phone}
            onChange={handleChange('phone')}
            onBlur={handleBlur('phone')}
            placeholder="050-000-0000"
            dir="ltr"
          />
          {errors.phone && <span className={styles.error}>{errors.phone}</span>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>כתובת מייל</label>
          <input
            type="email"
            className={styles.input}
            value={formData.email}
            disabled
            dir="ltr"
            style={{ opacity: 0.7, cursor: 'not-allowed' }}
          />
          <span className={styles.hint}>המייל נקבע בעת ההרשמה</span>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>מה התפקיד שלך?</label>
          <select
            className={`${styles.input} ${styles.select} ${errors.role ? styles.inputError : ''}`}
            value={formData.role}
            onChange={handleChange('role')}
            onBlur={handleBlur('role')}
          >
            <option value="">בחר תפקיד</option>
            {ROLE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.role && <span className={styles.error}>{errors.role}</span>}
        </div>
      </div>
    </div>
  );
};

export default UserInfoStep;
